<div class="ledger-container">
  <!-- Position: what the ledger accounts for, against what the ERP says is owed -->
  <div class="ledger-position">
    <div class="loading-state loading-state--inline" *ngIf="summaryLoading">
      <mat-spinner diameter="28"></mat-spinner>
    </div>

    <ng-container *ngIf="!summaryLoading">
      <div class="stat-grid" *ngIf="summary; else noSummary">
        <mat-card class="stat-card">
          <div class="stat-label">Ledger Balance</div>
          <div class="stat-value">{{ summary.currentBalance | currency }}</div>
          <div class="stat-sub">
            {{ summary.entryCount | number }} entries
            <ng-container *ngIf="summary.lastEntryDateTime">· last {{ businessDate(summary.lastEntryDateTime) }}</ng-container>
          </div>
        </mat-card>

        <mat-card class="stat-card">
          <div class="stat-label">ERP Balance</div>
          <div class="stat-value" *ngIf="summary.erpCustomerBalance !== null; else dash">
            {{ summary.erpCustomerBalance | currency }}
          </div>
          <div class="stat-sub" *ngIf="summary.erpCustomerBalanceAsOf">
            as stated {{ summary.erpCustomerBalanceAsOf | date : 'MMM dd, yyyy h:mm a' }}
          </div>
          <div
            class="stat-flag"
            *ngIf="showErpVariance && summary.erpBalanceVariance !== null && !isZero(summary.erpBalanceVariance)"
            matTooltip="Ledger balance minus the balance the ERP last stated. A stale ERP figure (the customer has had activity since) also shows here."
          >
            Ledger differs by {{ summary.erpBalanceVariance | currency }}
          </div>
        </mat-card>

        <mat-card class="stat-card">
          <div class="stat-label">Confirmation</div>
          <div class="bucket-line" [matTooltip]="bucketHints.verified">
            <span class="bucket-dot bucket-verified"></span>
            <span class="bucket-name">Verified</span>
            <span class="bucket-amount">{{ summary.verifiedBalance | currency }}</span>
          </div>
          <div class="bucket-line" [matTooltip]="bucketHints.unverified">
            <span class="bucket-dot bucket-unverified"></span>
            <span class="bucket-name">Sent to ERP</span>
            <span class="bucket-amount">{{ summary.unverifiedTotal | currency }}</span>
          </div>
          <div class="bucket-line" [matTooltip]="bucketHints.pending">
            <span class="bucket-dot bucket-pending"></span>
            <span class="bucket-name">Pending</span>
            <span class="bucket-amount">{{ summary.pendingTotal | currency }}</span>
          </div>
          <!--
            Only when there is some. Nothing will ever send these, so a line reading $0.00 on
            every customer of every profile that does not post to balances is noise — and an FTE
            predating the bucket reports it as `undefined`, where the same money is still inside
            Pending and there is nothing extra to say.
          -->
          <div class="bucket-line" *ngIf="summary.notApplicableTotal" [matTooltip]="bucketHints.notApplicable">
            <span class="bucket-dot bucket-notApplicable"></span>
            <span class="bucket-name">Not sent</span>
            <span class="bucket-amount">{{ summary.notApplicableTotal | currency }}</span>
          </div>
        </mat-card>

        <mat-card class="stat-card">
          <div class="stat-label" matTooltip="Open invoices less open credit memos, as the ERP's latest snapshots list them">Documents</div>
          <div class="stat-value">{{ position(summary).documents | currency }}</div>
          <div
            class="stat-sub"
            matTooltip="Money on the account that sits on no document: unapplied cash and open credit (the ERP's Open Credits)"
          >
            On account {{ position(summary).onAccount | currency }}
          </div>
        </mat-card>

        <mat-card class="stat-card" *ngIf="showOutOfBalance" [class.stat-card--alert]="!isZero(position(summary).difference)">
          <div
            class="stat-label"
            matTooltip="Ledger balance − documents − on account: what neither the documents nor the on-account money explain. Recorded as an observation — never as a ledger entry."
          >
            Out of Balance
          </div>
          <div class="stat-value">{{ position(summary).difference | currency }}</div>
          <div class="stat-sub">
            {{ summary.documentsOutOfBalanceCount | number }} document(s) out of balance
          </div>
          <div class="stat-sub" *ngIf="summary.documentsWithoutLedgerCount > 0">
            {{ summary.documentsWithoutLedgerCount | number }} with no ledger history
          </div>
          <!--
            Money on no document at all — the balance forward, unapplied cash, anything posted
            straight to the balance. Shown beside the variance because it is the usual reason the
            ledger balance and the documents differ, and it is NOT a discrepancy: the figure is
            reported, never subtracted from the variance above.
          -->

        </mat-card>

        <mat-card class="stat-card">
          <div class="stat-label">Coverage</div>
          <div class="stat-value stat-value--small" *ngIf="summary.coverageStartDate; else dash">
            since {{ dateOnly(summary.coverageStartDate) }}
          </div>
          <div
            class="stat-sub"
            *ngIf="summary.balanceForwardAmount !== null"
            matTooltip="Everything before coverage starts is one balance-forward entry: the ERP's balance at capture, less what the ledger itemises."
          >
            Balance forward {{ summary.balanceForwardAmount | currency }}
          </div>
        </mat-card>
      </div>
    </ng-container>

    <ng-template #noSummary>
      <div class="ledger-notice" *ngIf="!summaryFailed">
        <mat-icon>menu_book</mat-icon>
        <div>
          <strong>No ledger for this customer yet.</strong>
          <p>
            The ledger is built from the ERP's ledger and cash-receipt imports and from payments taken in Freely.
            It appears after the first of those reaches this customer.
          </p>
        </div>
      </div>
      <div class="ledger-notice ledger-notice--error" *ngIf="summaryFailed">
        <mat-icon>error_outline</mat-icon>
        <div><strong>The ledger position could not be loaded.</strong></div>
      </div>
    </ng-template>
    <ng-template #dash><div class="stat-value">—</div></ng-template>
  </div>

  <!-- Filters -->
  <div class="ledger-toolbar">
    <mat-chip-set class="type-filter" aria-label="Filter by entry type">
      <mat-chip class="type-chip" [class.chip-selected]="isAllTypes" (click)="selectAllTypes()">All</mat-chip>
      <mat-chip
        *ngFor="let type of entryTypes"
        class="type-chip"
        [class.chip-selected]="isTypeSelected(type)"
        [matTooltip]="typeLabels[type]"
        (click)="toggleType(type)"
        >{{ type }}</mat-chip
      >
    </mat-chip-set>

    <div class="toolbar-row">
      <mat-button-toggle-group
        class="bucket-filter"
        [(ngModel)]="bucket"
        (change)="onBucketChange()"
        aria-label="Filter by confirmation"
      >
        <mat-button-toggle value="">All</mat-button-toggle>
        <mat-button-toggle *ngFor="let b of buckets" [value]="b" [matTooltip]="bucketHints[b]">{{
          bucketLabels[b]
        }}</mat-button-toggle>
      </mat-button-toggle-group>

      <label class="date-filter">
        <span>From</span>
        <input type="date" [(ngModel)]="fromDate" (change)="onDateChange()" [max]="toDate || null" />
      </label>
      <label class="date-filter">
        <span>To</span>
        <input type="date" [(ngModel)]="toDate" (change)="onDateChange()" [min]="fromDate || null" />
      </label>

      <div class="ledger-search">
        <mat-icon class="search-icon">search</mat-icon>
        <input
          type="text"
          placeholder="Document #"
          [(ngModel)]="documentSearch"
          (ngModelChange)="onDocumentSearchChange()"
          aria-label="Only entries that move this document"
        />
        <button *ngIf="documentSearch" class="search-clear" type="button" (click)="clearDocumentSearch()" aria-label="Clear">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-slide-toggle
        [(ngModel)]="showCorrections"
        (change)="onCorrectionsChange()"
        matTooltip="Show corrected entries and their reversals. Off shows only the entries that stand."
      >
        Show corrections
      </mat-slide-toggle>

      <span class="toolbar-spacer"></span>

      <button mat-button type="button" (click)="toggleOrder()">
        <mat-icon>{{ order === 'desc' ? 'arrow_downward' : 'arrow_upward' }}</mat-icon>
        {{ order === 'desc' ? 'Newest first' : 'Oldest first' }}
      </button>
      <button mat-button type="button" color="primary" *ngIf="hasFilters" (click)="clearFilters()">Clear filters</button>
      <!--
        The one thing this screen writes. Shown only for a profile whose accounting works that
        way (`enableCustomerBalanceEntries`) and to an operator who may raise what a customer
        owes (`CUSTOMER_BALANCE_ADJUST`) — FTE checks both again.
      -->
      <button
        mat-flat-button
        color="primary"
        type="button"
        *ngIf="canPostBalanceEntry"
        (click)="openPostBalanceEntry()"
        matTooltip="Post a fee, credit or adjustment straight to this customer's balance, with no invoice behind it"
      >
        <mat-icon>post_add</mat-icon>
        Post to balance
      </button>
    </div>
  </div>

  <div class="loading-state" *ngIf="isLoading">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading ledger…</p>
  </div>

  <div class="empty-state" *ngIf="!isLoading && loadError">
    <mat-icon>error_outline</mat-icon>
    <h3>Could not load the ledger</h3>
    <p>{{ loadError }}</p>
  </div>

  <div class="table-container" *ngIf="!isLoading && !loadError && rows.length > 0">
    <table mat-table [dataSource]="rows" [trackBy]="trackRow" multiTemplateDataRows class="ledger-table">
      <ng-container matColumnDef="expand">
        <th mat-header-cell *matHeaderCellDef class="expand-column"></th>
        <td mat-cell *matCellDef="let entry" class="expand-column">
          <button mat-icon-button type="button" (click)="toggleExpand(entry)" matTooltip="Entry details">
            <mat-icon>{{ isExpanded(entry) ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </td>
      </ng-container>

      <ng-container matColumnDef="date">
        <th mat-header-cell *matHeaderCellDef>Date</th>
        <td mat-cell *matCellDef="let entry" class="nowrap">{{ businessDate(entry.entryDateTime) }}</td>
      </ng-container>

      <ng-container matColumnDef="type">
        <th mat-header-cell *matHeaderCellDef>Type</th>
        <td mat-cell *matCellDef="let entry">
          <span class="entry-type entry-type--{{ entry.entryType }}" [matTooltip]="typeLabel(entry)">{{
            entry.entryType
          }}</span>
          <span class="entry-type-label">{{ typeLabel(entry) }}</span>
          <span *ngIf="roleLabel(entry)" class="entry-role entry-role--{{ roleOf(entry) }}" [matTooltip]="roleHint(entry)">{{
            roleLabel(entry)
          }}</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="document">
        <th mat-header-cell *matHeaderCellDef>Document</th>
        <td mat-cell *matCellDef="let entry">
          <span *ngIf="documentLabel(entry); else noDoc">{{ documentLabel(entry) }}</span>
          <ng-template #noDoc><span class="muted">{{ entry.entryType === 'PMT' ? 'On account' : '—' }}</span></ng-template>
          <div class="entry-applied" *ngIf="entry.entryType === 'APL' && entry.appliedAmount !== null">
            applied {{ entry.appliedAmount | currency }}
          </div>
        </td>
      </ng-container>

      <ng-container matColumnDef="reference">
        <th mat-header-cell *matHeaderCellDef>Reference</th>
        <td mat-cell *matCellDef="let entry">{{ entry.erpReference || '—' }}</td>
      </ng-container>

      <ng-container matColumnDef="amount">
        <th mat-header-cell *matHeaderCellDef class="text-right">Amount</th>
        <td
          mat-cell
          *matCellDef="let entry"
          class="text-right amount-cell"
          [class.amount-credit]="entry.amount < 0"
          [class.amount-superseded]="!entry.active"
        >
          {{ entry.amount | currency }}
        </td>
      </ng-container>

      <ng-container matColumnDef="runningBalance">
        <th
          mat-header-cell
          *matHeaderCellDef
          class="text-right"
          matTooltip="The customer's balance after this entry, over every entry — filters never change it"
        >
          Balance
        </th>
        <td mat-cell *matCellDef="let entry" class="text-right balance-cell">{{ entry.runningBalance | currency }}</td>
      </ng-container>

      <ng-container matColumnDef="bucket">
        <th mat-header-cell *matHeaderCellDef>Confirmation</th>
        <td mat-cell *matCellDef="let entry">
          <span class="bucket-chip bucket-chip--{{ entry.bucket }}" [matTooltip]="bucketHint(entry)">{{ bucketLabel(entry) }}</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="source">
        <th mat-header-cell *matHeaderCellDef>Source</th>
        <td mat-cell *matCellDef="let entry" class="muted">{{ sourceLabel(entry) }}</td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: displayedColumns"
        [class.row-expanded]="isExpanded(row)"
        [class.row-superseded]="!row.active"
      ></tr>

      <ng-container matColumnDef="expandedDetail">
        <td mat-cell *matCellDef="let entry" [attr.colspan]="displayedColumns.length">
          <div class="expanded-detail" [@detailExpand]="isExpanded(entry) ? 'expanded' : 'collapsed'">
            <ng-container *ngIf="isExpanded(entry)">
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="detail-label">Ledger ID</span><span class="detail-value mono">{{ entry.ledgerId }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Source</span><span class="detail-value">{{ sourceLabel(entry) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">ERP Posting</span><span class="detail-value">{{ entry.erpPostingStatus }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Verified</span>
                  <span class="detail-value">{{
                    entry.erpVerified ? (entry.erpVerifiedAt ? (entry.erpVerifiedAt | date : 'MMM dd, yyyy h:mm a') : 'Yes') : 'No'
                  }}</span>
                </div>
                <div class="detail-item" *ngIf="entry.erpFeedsSeen.length > 0">
                  <span class="detail-label">Seen in ERP feeds</span>
                  <span class="detail-value">{{ entry.erpFeedsSeen.join(', ') }}</span>
                </div>
                <div class="detail-item" *ngIf="entry.freelyDocumentId">
                  <span class="detail-label">Freely {{ entry.freelyDocumentType }}</span>
                  <span class="detail-value mono"
                    >{{ entry.freelyDocumentId
                    }}<ng-container *ngIf="entry.freelyPaymentLineNumber !== null"> · line {{ entry.freelyPaymentLineNumber }}</ng-container></span
                  >
                </div>
                <div class="detail-item" *ngIf="entry.sourceAsOf">
                  <span class="detail-label">Statement as of</span>
                  <span class="detail-value">{{ entry.sourceAsOf | date : 'MMM dd, yyyy h:mm a' }}</span>
                </div>
                <div class="detail-item" *ngIf="entry.freelyAppliedDateTime">
                  <span class="detail-label">Recorded</span>
                  <span class="detail-value">{{ entry.freelyAppliedDateTime | date : 'MMM dd, yyyy h:mm a' }}</span>
                </div>
                <div class="detail-item" *ngIf="entry.entryType === 'APL'">
                  <span class="detail-label">Credit moved</span>
                  <span class="detail-value"
                    >{{ entry.appliedAmount | currency }} from {{ entry.appliesFromInvoiceRef || 'a payment' }} to
                    {{ entry.appliesToInvoiceRef || 'account' }}</span
                  >
                </div>
                <div class="detail-item" *ngIf="correctionReason(entry)">
                  <span class="detail-label">Correction</span>
                  <span class="detail-value">{{ correctionReason(entry) }}</span>
                </div>
                <div class="detail-item" *ngIf="isOperatorEntry(entry) && entry.extraFields?.['reason']">
                  <span class="detail-label">Reason</span>
                  <span class="detail-value">{{ entry.extraFields?.['reason'] }}</span>
                </div>
                <div class="detail-item" *ngIf="isOperatorEntry(entry) && entry.extraFields?.['postedByUserId']">
                  <span class="detail-label">Posted by</span>
                  <span class="detail-value">{{ entry.extraFields?.['postedByUserId'] }}</span>
                </div>
              </div>

              <!--
                Only an entry posted here, still active. An ERP feed owns what it wrote and a
                payment's entries are derived from the payment, so reversing either from this
                screen would be undone by the next run — FTE refuses both.
              -->
              <div class="detail-actions" *ngIf="canReverseEntry(entry)">
                <button mat-stroked-button color="warn" type="button" (click)="openReverseBalanceEntry(entry)">
                  <mat-icon>undo</mat-icon>
                  Reverse this entry
                </button>
                <span class="muted">A compensating entry is written; nothing is edited or deleted.</span>
              </div>

              <div class="chain" *ngIf="roleOf(entry) !== 'original'">
                <h4 class="chain-title">Correction chain</h4>
                <div class="chain-loading" *ngIf="chainLoading.has(entry.ledgerId)">
                  <mat-spinner diameter="20"></mat-spinner><span>Loading…</span>
                </div>
                <p class="muted" *ngIf="chainFailed.has(entry.ledgerId)">The rest of the chain could not be loaded.</p>
                <div class="chain-list" *ngIf="chainFor(entry).length > 0">
                  <div class="chain-row" *ngFor="let link of chainFor(entry)">
                    <span class="mono">{{ link.ledgerId }}</span>
                    <span class="entry-type entry-type--{{ link.entryType }}">{{ link.entryType }}</span>
                    <span class="entry-role entry-role--{{ roleOf(link) }}">{{ roleLabel(link) || 'Entry' }}</span>
                    <span>{{ businessDate(link.entryDateTime) }}</span>
                    <span class="chain-amount">{{ link.amount | currency }}</span>
                  </div>
                </div>
              </div>
            </ng-container>
          </div>
        </td>
      </ng-container>

      <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="detail-row"></tr>
    </table>

    <mat-paginator
      [length]="total"
      [pageSize]="pageSize"
      [pageIndex]="pageIndex"
      [pageSizeOptions]="pageSizeOptions"
      (page)="onPage($event)"
      showFirstLastButtons
      aria-label="Select page of ledger entries"
    ></mat-paginator>
  </div>

  <div class="empty-state" *ngIf="!isLoading && !loadError && rows.length === 0">
    <mat-icon>menu_book</mat-icon>
    <h3>No ledger entries</h3>
    <p *ngIf="hasFilters">No entries match the current filters.</p>
    <p *ngIf="!hasFilters">This customer has no ledger entries yet.</p>
  </div>
</div>
